import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Button, MenuItem, Stack } from '@mui/material'
import { ProjectModel, ProjectModelType } from '@/types/project-models'
import { OverlayState } from '@/hooks/useOverlay'
import { useGetDataLists } from '@/service-library/hooks/data-lists'
import { useCreateProjectModelWithDefaults } from '@/service-library/hooks/project-models'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import queryKeys from '@/service-library/query-keys'
import generateUuid from '@/utils/uuid'
import { showInfoSnackbar } from '@/utils/snackbars'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'
import { NER_MODEL_TYPE_ID } from '@/utils//model-page'
import { useRootOrganization } from '@/components/organizations/RootOrganizationProvider'

type AddCustomModelDialogProps = {
  overlay: OverlayState
  modelType: ProjectModelType | null
}

export default function AddCustomModelDialog({
  overlay,
  modelType,
}: AddCustomModelDialogProps) {
  const { project } = useProjectContext()
  const { rootOrganization } = useRootOrganization()
  const navigate = useNavigate()

  const isCategoryModel = modelType?.code === 'Category'

  const { dataLists } = useGetDataLists({
    filters: {
      limit: '1000',
      org_id: rootOrganization.id,
    },
    enabled: isCategoryModel,
  })

  const { createProjectModelWithDefaults } = useCreateProjectModelWithDefaults({
    sideEffectQueryKeys: [
      queryKeys.projectLinkedModels.all,
      queryKeys.workflows.lists(),
      queryKeys.projectModels.lists(),
    ],
    onSettled: (projectModel) => {
      if (projectModel?.project_model_type_id === NER_MODEL_TYPE_ID) {
        setTimeout(() => {
          navigate(
            `../model-configuration?workflow=${projectModel.training_workflow_id}`,
          )
        }, 2000)
      }
    },
  })

  const methods = useForm({
    defaultValues: {
      name: '',
      data_list_id: '',
    },
  })

  function onSubmit(values: { name: string; data_list_id: string }) {
    createProjectModelWithDefaults({
      id: generateUuid(),
      project_id: project.id,
      target_accuracy: '0.90',
      name: values.name,
      project_model_type_id: modelType?.id || '',
      is_content_category_whole_document: false, // FIXME -- How do we get this if the field isn't attached yet?
      is_content_category_rows: false, // FIXME -- Don't category models need the list?
      data_list_id: values.data_list_id || null,
    } as ProjectModel)
    showInfoSnackbar('Creating model...')
    overlay.close()
  }

  useEffect(() => {
    if (!overlay.isOpen) methods.reset()
  }, [methods, overlay.isOpen])

  return (
    <Dialog
      {...overlay}
      title={`Create Custom ${modelType?.name} Model`}
      maxWidth="xs"
    >
      <PixyDocsForm methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <FormTextField name="name" label="Name" autoFocus required />

            {isCategoryModel && (
              <FormTextField
                select
                name="data_list_id"
                id="demo-simple-select"
                label="List"
                required
              >
                {dataLists.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name}
                  </MenuItem>
                ))}
              </FormTextField>
            )}
          </Stack>
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit">Create</Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
